import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Building2, CalendarDays, CalendarClock, GraduationCap, Hourglass, Images, LayoutDashboard, Loader2, Lock, LogOut, Mail, MapPinned, Megaphone, Menu, MessageSquareQuote, Package, Percent, Receipt, Scale, Search, ShieldCheck, Star, Store, Tags, Truck, Users, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  {
    title: "Général",
    links: [{ to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" }],
  },
  {
    title: "Catalogue",
    links: [
      { to: "/admin/accueil", label: "Produit à la une", icon: Megaphone, testid: "admin-nav-spotlight" },
      { to: "/admin/produits", label: "Produits", icon: Package, testid: "admin-nav-produits" },
      { to: "/admin/categories", label: "Catégories", icon: Tags, testid: "admin-nav-categories" },
      { to: "/admin/medias", label: "Bibliothèque d'images", icon: Images, testid: "admin-nav-medias" },
      { to: "/admin/tarifs", label: "Tarifs", icon: Percent, testid: "admin-nav-tarifs" },
    ],
  },
  {
    title: "Contenu",
    links: [
      { to: "/admin/gazette", label: "Gazette", icon: BookOpen, testid: "admin-nav-gazette" },
      { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, testid: "admin-nav-agenda" },
    ],
  },
  {
    title: "Ventes",
    links: [
      { to: "/admin/commandes", label: "Commandes", icon: Receipt, testid: "admin-nav-commandes" },
      { to: "/admin/magasins", label: "Magasins", icon: Building2, testid: "admin-nav-magasins" },
      { to: "/admin/ecoles", label: "Écoles", icon: GraduationCap, testid: "admin-nav-ecoles" },
      { to: "/admin/abonnements", label: "Livraisons abonnements", icon: CalendarClock, testid: "admin-nav-abonnements" },
      { to: "/admin/livraison", label: "Livraison", icon: Truck, testid: "admin-nav-livraison" },
      { to: "/admin/tournees", label: "Tournées", icon: MapPinned, testid: "admin-nav-tournees" },
      { to: "/admin/fidelite", label: "Fidélité", icon: Star, testid: "admin-nav-fidelite" },
      { to: "/admin/avis", label: "Avis clients", icon: MessageSquareQuote, testid: "admin-nav-avis" },
    ],
  },
  {
    title: "Clients",
    links: [
      { to: "/admin/clients", label: "Comptes clients", icon: Users, testid: "admin-nav-clients" },
      { to: "/admin/newsletter", label: "Newsletter", icon: Mail, testid: "admin-nav-newsletter" },
      { to: "/admin/liste-attente", label: "Liste d'attente", icon: Hourglass, testid: "admin-nav-waitlist" },
    ],
  },
  {
    title: "Site",
    links: [
      { to: "/admin/seo", label: "Référencement", icon: Search, testid: "admin-nav-seo" },
      { to: "/admin/securite", label: "Sécurité (2FA)", icon: ShieldCheck, testid: "admin-nav-securite" },
      { to: "/admin/legal", label: "Mentions & RGPD", icon: Scale, testid: "admin-nav-legal" },
    ],
  },
];

const ALL_LINKS = GROUPS.flatMap((g) => g.links);

function currentLabel(pathname) {
  // meilleur match : lien exact, sinon le plus long préfixe
  let best = null;
  for (const l of ALL_LINKS) {
    if (l.end ? pathname === l.to : pathname === l.to || pathname.startsWith(l.to + "/")) {
      if (!best || l.to.length > best.to.length) best = l;
    }
  }
  return best?.label || "Administration";
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (user === null) {
    return (
      <div data-testid="admin-loading" className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={28} />
        <span className="font-serif text-xl">Vérification de la session…</span>
      </div>
    );
  }
  if (user === false || (user && user.role !== "admin")) return <Navigate to="/admin/login" replace />;
  const needs2fa = user.totp_enabled === false;
  if (needs2fa && pathname !== "/admin/securite") return <Navigate to="/admin/securite" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-amber-deep text-white shadow-md shadow-amber-900/15" : "text-bark-soft hover:bg-sand hover:text-bark"
    }`;

  const GroupedNav = ({ onNavigate }) => (
    <nav className="flex flex-col gap-0.5" data-testid="admin-nav">
      {GROUPS.map((group) => (
        <div key={group.title} className="mb-2 flex flex-col gap-0.5">
          <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-mute">{group.title}</p>
          {group.links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid} onClick={onNavigate}
              aria-disabled={needs2fa && l.to !== "/admin/securite"}
              title={needs2fa && l.to !== "/admin/securite" ? "Activez d'abord la double authentification" : undefined}
              className={(p) => `${linkClass(p)} ${needs2fa && l.to !== "/admin/securite" ? "pointer-events-none opacity-40" : ""}`}>
              <l.icon size={17} /> {l.label}
              {needs2fa && l.to !== "/admin/securite" && <Lock size={12} className="ml-auto" />}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-layout" className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-8 md:flex-row md:gap-8 md:py-10">
      {/* Barre supérieure mobile */}
      <div className="sticky top-0 z-30 -mx-4 flex items-center justify-between gap-3 border-b border-amber-800/15 bg-cream/95 px-4 py-3 backdrop-blur md:hidden">
        <button
          data-testid="admin-mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="flex items-center gap-2 rounded-xl border border-amber-800/20 bg-white px-3.5 py-2 text-sm font-semibold text-bark shadow-sm active:scale-95"
        >
          <Menu size={18} /> Menu
        </button>
        <span className="truncate font-serif text-base font-semibold text-bark">{currentLabel(pathname)}</span>
      </div>

      {/* Tiroir mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-bark/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-[82%] max-w-xs flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-amber-800/10 px-4 py-4">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-mute">Miellerie — Admin</p>
              <button data-testid="admin-mobile-close" onClick={() => setMobileOpen(false)} className="rounded-full p-1.5 text-bark-soft hover:bg-sand hover:text-bark">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3">
              <GroupedNav onNavigate={() => setMobileOpen(false)} />
            </div>
            <div className="flex flex-col gap-1 border-t border-amber-900/10 px-3 py-3">
              <Link to="/" onClick={() => setMobileOpen(false)} data-testid="admin-view-shop-mobile" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:bg-sand hover:text-bark">
                <Store size={17} /> Voir la boutique
              </Link>
              <button data-testid="admin-logout-mobile" onClick={handleLogout} className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-terra transition-colors hover:bg-terra/10">
                <LogOut size={17} /> Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barre latérale desktop */}
      <aside className="hidden shrink-0 md:block md:w-60">
        <div className="rounded-2xl border border-amber-800/15 bg-white p-4 shadow-sm md:sticky md:top-24 md:max-h-[calc(100vh-8rem)] md:overflow-y-auto">
          <p className="px-3 pb-3 text-xs uppercase tracking-[0.2em] text-stone-mute">Miellerie — Admin</p>
          <GroupedNav />
          <div className="mt-3 flex flex-col gap-1 border-t border-amber-900/10 pt-3">
            <Link to="/" data-testid="admin-view-shop" className="flex items-center gap-3 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:bg-sand hover:text-bark">
              <Store size={17} /> Voir la boutique
            </Link>
            <button data-testid="admin-logout" onClick={handleLogout} className="flex items-center gap-3 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium text-terra transition-colors hover:bg-terra/10">
              <LogOut size={17} /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
